import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container,
  Paper,
  Input
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useOrg } from '../context/OrgContext';

const ACCEPTED_TYPES = 'application/pdf,image/jpeg,image/png,image/heic,image/webp,.pdf,.jpg,.jpeg,.png,.heic,.webp';

/**
 * Where the "Request COI" email sends an applicant. The link carries a code
 * for one policy on their application, so no login is needed.
 */
function CoiUploadPage() {
  const { name } = useOrg();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [request, setRequest] = useState(null); // { companyName, policyType, policyId? }
  const [linkError, setLinkError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // 'uploading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setLinkError(`This page needs the link from your email. Please open the "Upload Certificate of Insurance" link in the email from ${name}.`);
      return;
    }
    fetch(`${process.env.REACT_APP_API_URL}/api/applications/${id}/coi-request?token=${encodeURIComponent(token)}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.message || 'This upload link is invalid or has expired.');
        setRequest(data);
      })
      .catch((err) => setLinkError(err.message));
  }, [id, token, name]);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] || null);
    setUploadStatus(null);
    setMessage('');
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('error');
      setMessage('Please choose a file first.');
      return;
    }
    setLoading(true);
    setUploadStatus('uploading');
    setMessage('Uploading…');

    const formData = new FormData();
    formData.append('coi', selectedFile);
    formData.append('token', token);
    // The link only carries the token; the server resolves it to a policy and
    // may hand back that policy's id, which pins the upload to the right record
    if (request?.policyId) formData.append('policyId', request.policyId);
    if (request?.policyType) formData.append('policyType', request.policyType);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${id}/upload-coi-public`, {
        method: 'POST',
        body: formData
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok) {
        setUploadStatus('success');
        setMessage(`Thank you! Your Certificate of Insurance was received. ${name} will review it.`);
        setSelectedFile(null);
      } else {
        setUploadStatus('error');
        setMessage(result.message || 'The upload failed. Please try again.');
      }
    } catch (error) {
      setUploadStatus('error');
      setMessage('The upload failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, px: 2 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Upload Certificate of Insurance
        </Typography>

        {linkError ? (
          <Alert severity="error">{linkError}</Alert>
        ) : !request ? (
          <CircularProgress />
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>{request.companyName}</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Policy: <b>{request.policyType}</b>. Upload the certificate as a PDF or a clear photo.
            </Typography>

            {uploadStatus === 'error' && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}
            {uploadStatus === 'success' && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {uploadStatus === 'uploading' && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

            <Box sx={{ mb: 3 }}>
              <Input
                type="file"
                inputProps={{ accept: ACCEPTED_TYPES }}
                onChange={handleFileChange}
                sx={{ display: 'none' }}
                id="coi-file-upload"
              />
              <label htmlFor="coi-file-upload">
                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />}>
                  {selectedFile ? selectedFile.name : 'Choose PDF or photo'}
                </Button>
              </label>
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={!selectedFile || loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : 'Upload Certificate'}
            </Button>

            {uploadStatus === 'success' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Sent the wrong file? Choose another and upload again; the newest one is used.
              </Typography>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}

export default CoiUploadPage;
